import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, ScrollRestoration, Navigate } from 'react-router-dom';
import SignUpPage from './pages/client/auth/SignUpPage';
import SignInPage from './pages/client/auth/SignInPage';
import ResetPasswordPage from './pages/client/auth/ResetPasswordPage';
import EmailVerificationPage from './pages/client/auth/EmailVerificationPage';
import IndexPage from './pages/client/IndexPage';
import PortvillePage from './pages/client/PortvillePage';
import PortvilleBillingPage from './pages/client/portville/PortvilleBillingPage';
import PortvilleBillingAuthPage from './pages/client/portville/PortvilleBillingAuthPage';
import WaitingPage from './pages/client/portville/WaitingPage';
import DetailPage from './pages/client/portville/DetailPage';
import CodeApplyPage from './pages/client/portville/CodeApplyPage';
import LotteryPage from './pages/client/portville/LotteryPage';
import ContractPage from './pages/client/portville/ContractPage';
import ResidencyPage from './pages/client/portville/ResidencyPage';
import MoveOutRequestPage from './pages/client/portville/MoveOutRequestPage';
import ContractExtensionPage from './pages/client/portville/ContractExtensionPage';
import ParkingMainPage from './pages/client/parking/ParkingMainPage';
import ParkingApplicationPage from './pages/client/parking/ParkingApplicationPage';
import ParkingDetailPage from './pages/client/parking/ParkingDetailPage';
import FacilityReservationPage from './pages/client/facility/FacilityReservationPage';
import FacilityReservationConfirmPage from './pages/client/facility/FacilityReservationConfirmPage';
import FacilityReservationListPage from './pages/client/facility/FacilityReservationListPage';
import ClientFacilityReservationDetailPage from './pages/client/facility/FacilityReservationDetailPage';
import AdminSignInPage from './pages/admin/auth/AdminSignInPage';
import DashboardPage from './pages/admin/dashboard/DashboardPage';
import UserManagePage from './pages/admin/users/UserManagePage';
import UserDetailPage from './pages/admin/users/UserDetailPage';
import UserEditPage from './pages/admin/users/UserEditPage';
import PortvilleManagePage from './pages/admin/portville/PortvilleManagePage';
import PortvilleDetailPage from './pages/admin/portville/PortvilleDetailPage';
import FacilityListPage from './pages/admin/facilities/FacilityListPage';
import FacilityDetailPage from './pages/admin/facilities/FacilityDetailPage';
import FacilityNewPage from './pages/admin/facilities/FacilityNewPage';
import AdminLayout from './components/admin/layout/AdminLayout';
import UserAddPage from './pages/admin/users/UserAddPage';
import AdminManagePage from './pages/admin/admins/AdminManagePage';
import FacilityReservationsPage from './pages/admin/facility-reservations/FacilityReservationsPage';
import FacilityReservationsCalendarPage from './pages/admin/facility-reservations/FacilityReservationsCalendarPage';
import AdminFacilityReservationDetailPage from './pages/admin/facility-reservations/FacilityReservationDetailPage';
import SettingsPageClient from './pages/client/settings/SettingsPage';
import LevelUpRequestPage from './pages/client/settings/LevelUpRequestPage';
import AdminSettingsPage from './pages/admin/settings/SettingsPage';
import EmailPolicyPage from './pages/client/EmailPolicyPage';
import PrivacyPolicyPage from './pages/client/PrivacyPolicyPage';
import NoticePage from './pages/client/NoticePage';
import NoticeDetailPage from './pages/client/NoticeDetailPage';
import NoticeManagePage from './pages/admin/notices/NoticeManagePage';
import PortvilleRoomManagePage from './pages/admin/portville-rooms/PortvilleRoomManagePage';
import PortvilleLotteryManagePage from './pages/admin/portville-lottery/PortvilleLotteryManagePage';
import RoomDetailPage from './pages/admin/portville-rooms/RoomDetailPage';
import RoomEditPage from './pages/admin/portville-rooms/RoomEditPage';
import RoomAddPage from './pages/admin/portville-rooms/RoomAddPage';
import ContractSignaturePage from './pages/admin/portville-rooms/ContractSignaturePage';
import ViewContractPage from './pages/admin/portville-rooms/ViewContractPage';
import MoveInInspectionPage from './pages/admin/portville-rooms/MoveInInspectionPage';
import MoveOutInspectionPage from './pages/admin/portville-rooms/MoveOutInspectionPage';
import ExtensionRequestPage from './pages/admin/portville-rooms/ExtensionRequestPage';
import ExtensionContractPage from './pages/admin/portville-rooms/ExtensionContractPage';
import BillingSettingsPage from './pages/admin/portville/billing-settings/BillingSettingsPage';
import BillsManagePage from './pages/admin/portville/bills/BillsManagePage';
import UsageManagePage from './pages/admin/portville/usage/UsageManagePage';
import ParkingManagePage from './pages/admin/parking/ParkingManagePage';
import ParkingLotteryPage from './pages/admin/parking/ParkingLotteryPage';
import GuidePage from './pages/client/GuidePage';
import SitePreparationPage from './pages/client/SitePreparationPage';
import InquiryListPage from './pages/client/inquiry/InquiryListPage';
import InquiryCreatePage from './pages/client/inquiry/InquiryCreatePage';
import InquiryDetailPage from './pages/client/inquiry/InquiryDetailPage';
import AdminInquiryManagePage from './pages/admin/inquiry/InquiryManagePage';
import AdminInquiryDetailPage from './pages/admin/inquiry/InquiryDetailPage';
import './index.css';

// Root layout component that provides ScrollRestoration
const RootLayout = () => {
  return (
    <div className="bg-gray-100 min-h-screen">
      <Outlet />
      <ScrollRestoration
        getKey={(location, matches) => {
          // 호실관리 페이지에서는 데이터 로딩 상태를 고려한 스크롤 복원
          if (location.pathname === '/admin/portville-rooms') {
            return location.pathname;
          }
          // 다른 페이지는 기본 동작
          return location.key;
        }}
      />
    </div>
  );
};

// Create the router using createBrowserRouter
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { path: "signup", element: <SignUpPage /> },
      { path: "signin", element: <SignInPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },
      { path: "email-verification", element: <EmailVerificationPage /> },
      { path: "guide", element: <GuidePage /> },
      { path: "site-preparation", element: <SitePreparationPage /> },
      { path: "email-policy", element: <EmailPolicyPage /> },
      { path: "privacy", element: <PrivacyPolicyPage /> },
      { path: "notices", element: <NoticePage /> },
      { path: "notices/:id", element: <NoticeDetailPage /> },
      { path: "admin/signin", element: <AdminSignInPage /> },
      {
        path: "admin",
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "portville", element: <PortvilleManagePage /> },
          { path: "portville/:id", element: <PortvilleDetailPage /> },
          { path: "portville/applications/:id", element: <PortvilleDetailPage /> },
          { path: "reservations", element: <div>예약 관리</div> },
          { path: "users", element: <UserManagePage /> },
          { path: "users/add", element: <UserAddPage /> },
          { path: "users/:userId", element: <UserDetailPage /> },
          { path: "users/:userId/edit", element: <UserEditPage /> },
          { path: "facilities", element: <FacilityListPage /> },
          { path: "facilities/new", element: <FacilityNewPage /> },
          { path: "facilities/:id", element: <FacilityDetailPage /> },
          { path: "facility-reservations", element: <FacilityReservationsPage /> },
          { path: "facility-reservations/calendar", element: <FacilityReservationsCalendarPage /> },
          { path: "facility-reservations/:id", element: <AdminFacilityReservationDetailPage /> },
          { path: "portville-rooms", element: <PortvilleRoomManagePage /> },
          { path: "portville-lottery", element: <PortvilleLotteryManagePage /> },
          { path: "portville-rooms/add", element: <RoomAddPage /> },
          { path: "portville-rooms/detail/:roomId", element: <RoomDetailPage /> },
          { path: "portville-rooms/edit/:roomId", element: <RoomEditPage /> },
          { path: "portville-rooms/contract/:roomId", element: <ContractSignaturePage /> },
          { path: "portville-rooms/view-contract/:roomId", element: <ViewContractPage /> },
          { path: "portville-rooms/move-in-inspection/:roomId", element: <MoveInInspectionPage /> },
          { path: "portville-rooms/move-out-inspection/:roomId", element: <MoveOutInspectionPage /> },
          { path: "portville-rooms/extension-request/:roomId", element: <ExtensionRequestPage /> },
          { path: "portville-rooms/extension-contract/:roomId", element: <ExtensionContractPage /> },
          { path: "portville/billing-settings", element: <BillingSettingsPage /> },
          { path: "portville/bills", element: <BillsManagePage /> },
          { path: "portville/usage", element: <UsageManagePage /> },
          { path: "parking", element: <ParkingManagePage /> },
          { path: "parking/lottery", element: <ParkingLotteryPage /> },
          { path: "notices", element: <NoticeManagePage /> },
          { path: "inquiry", element: <AdminInquiryManagePage /> },
          { path: "inquiry/:id", element: <AdminInquiryDetailPage /> },
          { path: "settings", element: <AdminSettingsPage /> },
          { path: "admins", element: <AdminManagePage /> }
        ]
      },
      { index: true, element: <IndexPage /> },
      { path: "main", element: <IndexPage /> },
      { path: "portville", element: <PortvillePage /> },
      { path: "portville/billing", element: <PortvilleBillingPage /> },
      { path: "portville/billing/auth", element: <PortvilleBillingAuthPage /> },
      { path: "portville/waiting", element: <WaitingPage /> },
      { path: "portville/detail", element: <DetailPage /> },
      { path: "portville/code", element: <CodeApplyPage /> },
      { path: "portville/lottery", element: <LotteryPage /> },
      { path: "portville/contract", element: <ContractPage /> },
      { path: "portville/residency", element: <ResidencyPage /> },
      { path: "portville/move-out-request", element: <MoveOutRequestPage /> },
      { path: "portville/contract-extension", element: <ContractExtensionPage /> },
      { path: "parking", element: <ParkingMainPage /> },
      { path: "parking/application", element: <ParkingApplicationPage /> },
      { path: "parking/detail", element: <ParkingDetailPage /> },
      { path: "facility", element: <FacilityReservationPage /> },
      { path: "facility/confirm", element: <FacilityReservationConfirmPage /> },
      { path: "facility/reservations", element: <FacilityReservationListPage /> },
      { path: "facility/reservations/:id", element: <ClientFacilityReservationDetailPage /> },
      { path: "inquiry", element: <InquiryListPage /> },
      { path: "inquiry/create", element: <InquiryCreatePage /> },
      { path: "inquiry/:id", element: <InquiryDetailPage /> },
      { path: "settings", element: <SettingsPageClient /> },
      { path: "level-up-request", element: <LevelUpRequestPage /> }
    ]
  }
]);

function App() {
  // 개발 환경에서만 검증 스크립트 로드
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('./utils/validateRefactoring').then(module => {
        (window as any).validateRefactoring = module.validateRefactoring;
        //console.log('🔍 리팩토링 검증 함수가 로드되었습니다. 콘솔에서 validateRefactoring()을 실행하세요.');
      }).catch(err => {
        //console.warn('검증 스크립트 로드 실패:', err);
      });
    }
  }, []);

  // 브라우저의 자동 스크롤 복원 비활성화 (React Router가 완전히 제어)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
